import { useCallback } from "react";
import { ApplyScheduleTemplateBody } from "@common/schemas/domains/accounting/scheduleApplication.schema";
import { ScheduleRecognitionOverrides } from "@common/schemas/domains/accounting/scheduleTemplate.schema";
import { ScheduleSourceType } from "@common/types/domains/accounting/schedule";
import { useToaster } from "@design-system/ToastMessages/useToaster";
import {
    ShowLockedPeriods,
    useScheduleLockedPeriodsDialog,
} from "@components/accounting/schedules/useScheduleLockedPeriodsDialog";
import { useApplyScheduleWithLinkedConflict } from "@components/accounting/schedules/linkedConflict/useApplyScheduleWithLinkedConflict";

export interface InvoiceScheduleApplyTarget {
    entityId: number;
    invoiceId: string;
    amount: number;
    /** Set for a line-scoped apply; omitted applies to the whole invoice. */
    sourceLineIds?: string[];
    /** Required only when the template has a Specific date component. */
    specificDate?: string;
    /** A start the schedule modal moved off the invoice date. */
    termStart?: string;
    /** Rows or accounts edited in the schedule modal before applying. */
    overrides?: ScheduleRecognitionOverrides;
}

export function buildInvoiceSchedulePayload(
    target: InvoiceScheduleApplyTarget,
): ApplyScheduleTemplateBody {
    return {
        sourceType: ScheduleSourceType.invoice,
        entityId: target.entityId,
        invoiceId: target.invoiceId,
        amount: Math.abs(target.amount),
        ...(target.sourceLineIds
            ? { sourceLineIds: target.sourceLineIds }
            : {}),
        ...(target.specificDate ? { specificDate: target.specificDate } : {}),
        ...(target.termStart ? { termStart: target.termStart } : {}),
        ...(target.overrides ? { overrides: target.overrides } : {}),
    };
}

/**
 * Single apply call site for every invoice schedule surface — the two grid
 * cells and the Specific-date confirm step — so payload shape, toast, and
 * error handling cannot drift apart between them.
 */
export function useApplyInvoiceSchedule({
    showLockedPeriods: showLocalLockedPeriods,
}: { showLockedPeriods?: ShowLockedPeriods } = {}) {
    const { toast, error } = useToaster();
    const { applyWithLinkedConflict, linkedConflictModal, isApplying } =
        useApplyScheduleWithLinkedConflict({
            showLockedPeriods: showLocalLockedPeriods,
        });
    const showLockedPeriods = useScheduleLockedPeriodsDialog(
        showLocalLockedPeriods,
    );

    const applySchedule = useCallback(
        async (options: {
            templateId: string;
            target: InvoiceScheduleApplyTarget;
            onApplied?: () => Promise<void> | void;
        }): Promise<boolean> => {
            try {
                const result = await applyWithLinkedConflict({
                    templateId: options.templateId,
                    payload: buildInvoiceSchedulePayload(options.target),
                    onApplied: options.onApplied,
                });
                // The conflict modal finishes the apply; not a failure.
                if (result.outcome === "awaitingChoice") {
                    return true;
                }
                toast("Schedule applied");
                await options.onApplied?.();
                return true;
            } catch (applyError) {
                if (!showLockedPeriods(applyError)) {
                    error(
                        applyError instanceof Error
                            ? applyError.message
                            : "Failed to apply schedule",
                    );
                }
                return false;
            }
        },
        [applyWithLinkedConflict, error, showLockedPeriods, toast],
    );

    return { applySchedule, isApplying, linkedConflictModal };
}
